import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Skeleton } from "@mui/material";
import { Phone, Notifications, Timeline } from "@mui/icons-material";
import { useSelector } from "react-redux";
import axios from "axios";
import MetricCard from "components/contacts/MetricCard";
import DetailedMetricsList from "components/contacts/DetailedMetricsList";
import CallLogsList from "components/contacts/CallLogsList";
import FilterDrawer from "components/contacts/FilterDrawer";
import ContactDetails from "components/contacts/ContactDetails";

export default function AgentDashboard() {
  const { domain, token, userDetails } = useSelector(
    (state) => state.tokenReducer
  );

  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsMap, setContactsMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const loggedInUserId = userDetails?.id;
  const todayDDMMYYYY = (() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  })();
  const [filters, setFilters] = useState({
    userId: loggedInUserId || "all",
    contactId: "all",
    fromDate: todayDDMMYYYY,
    toDate: todayDDMMYYYY,
    statusId: "all",
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        setLoadingStatus(true);
        const res = await axios.get(`${domain}crm/contact-status/`, {
          headers: { Authorization: `token ${token}` },
        });
        console.log(res);
        setStatuses(res.data || []);
      } catch (err) {
        console.error("Failed to load statuses", err);
      } finally {
        setLoadingStatus(false);
      }
    };
    loadStatuses();
  }, [domain, token]);

  const toApiDate = (ddmmyyyy) => {
    if (!ddmmyyyy) return null;
    const [dd, mm, yyyy] = ddmmyyyy.split("/");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const userRes = await axios.get(
          `${domain}crm/product-based/user/?product=CRM`,
          {
            headers: { Authorization: `token ${token}` },
          }
        );
        const contactRes = await axios.get(`${domain}crm/contacts/`, {
          headers: { Authorization: `token ${token}` },
        });

        setUsers(userRes.data);
        setContacts(contactRes.data);

        const map = {};
        contactRes.data.forEach((c) => (map[c.id] = c));
        setContactsMap(map);
      } catch (err) {
        console.error("Failed to load initial data", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [domain, token, userDetails.id]);

  useEffect(() => {
    if (!filters.userId || !filters.fromDate || !filters.toDate) return;

    const loadAgentData = async () => {
      setIsLoading(true);
      try {
        const assignedParam =
          filters.userId !== "all" ? `&assigned_to=${filters.userId}` : "";
        const contactParam =
          filters.contactId !== "all" ? `&contact_id=${filters.contactId}` : "";
        const statusParam =
          filters.statusId !== "all" ? `&status_id=${filters.statusId}` : "";

        const from = toApiDate(filters.fromDate);
        const to = toApiDate(filters.toDate);

        const qs = `${contactParam}${statusParam}${assignedParam}&start_date=${from}&end_date=${to}`;

        const [activitiesRes, remindersRes, callsRes] = await Promise.all([
          axios.get(`${domain}crm/activities/?${qs.replace(/^&/, "")}`, {
            headers: { Authorization: `token ${token}` },
          }),
          axios.get(`${domain}crm/reminders/?${qs.replace(/^&/, "")}`, {
            headers: { Authorization: `token ${token}` },
          }),
          axios.get(`${domain}crm/calls/?${qs.replace(/^&/, "")}`, {
            headers: { Authorization: `token ${token}` },
          }),
        ]);

        const activities = activitiesRes.data || [];
        const reminders = remindersRes.data || [];
        const calls = callsRes.data || [];

        const callActivities = activities?.filter(
          (a) => a.activity_name.toLowerCase() === "call"
        );

        setMetrics({
          summary: {
            activityCount: activities?.length,
            callCount: calls?.length,
            reminderCount: reminders?.length,
          },
          lists: {
            activities,
            calls: callActivities,
            reminders,
          },
        });
      } catch (error) {
        console.error("Error loading agent data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAgentData();
  }, [filters, domain, token, users]);

  const userContacts =
    filters.userId && filters.userId !== "all"
      ? contacts?.filter((c) => c.assigned_to === filters.userId)
      : contacts;
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Agent Dashboard
          </Typography>
          <Typography color="text.secondary" mb={3}>
            Track daily metrics and call logs for your sales team.
          </Typography>
        </Box>

        <FilterDrawer
          users={users}
          userContacts={userContacts}
          loggedInUserId={loggedInUserId}
          currentFilters={filters}
          onApplyFilters={setFilters}
          statuses={statuses}
          loadingStatus={loadingStatus}
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          {isLoading || !metrics ? (
            <Skeleton
              variant="rectangular"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <MetricCard
              title="Total Activities"
              value={metrics.summary.activityCount}
              icon={Timeline}
              color="primary"
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {isLoading || !metrics ? (
            <Skeleton
              variant="rectangular"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <MetricCard
              title="Calls Made"
              value={metrics.summary.callCount}
              icon={Phone}
              color="success"
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {isLoading || !metrics ? (
            <Skeleton
              variant="rectangular"
              height={100}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <MetricCard
              title="Reminders Due"
              value={metrics.summary.reminderCount}
              icon={Notifications}
              color="warning"
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <DetailedMetricsList
            activities={metrics?.lists.activities || []}
            calls={metrics?.lists.calls || []}
            reminders={metrics?.lists.reminders || []}
            isLoading={isLoading}
            contactsMap={contactsMap}
            onContactSelect={setSelectedContact}
            selectedActivityId={selectedActivityId}
            onActivitySelect={setSelectedActivityId}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          {selectedContact ? (
            <ContactDetails
              contact={selectedContact}
              onClose={() => setSelectedContact(null)}
              onUpdateContact={(updatedContact) => {
                if (updatedContact && updatedContact.id) {
                  setContacts((prev) =>
                    prev.map((c) =>
                      c.id === updatedContact.id ? updatedContact : c
                    )
                  );
                  setSelectedContact(updatedContact);
                }
              }}
            />
          ) : (
            <CallLogsList
              selectedFromDate={toApiDate(filters.fromDate)}
              selectedToDate={toApiDate(filters.toDate)}
              selectedAgent={filters.userId !== "all" ? filters.userId : null}
              selectedContact={
                filters.contactId !== "all" ? filters.contactId : null
              }
              statusId={filters.statusId || null}
              contactsMap={contactsMap}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
